import c from '@/styles/ContentMarkup';
import { ClassNameProp, Component, ComponentProps } from '@/types/component';
import { cn } from 'lib/utils/cn';
import { getPropStylesFactory } from 'lib/utils/getPropStylesFactory';
import Script from 'next/script';

export interface MarkupProps extends ComponentProps<'div'> {
  markup?: string;
  variant?: ClassNameProp<'default'>;
}

const transformScriptTags = (dataContentIndex: string | number, scriptTags: string[]) => {
  const transformedScriptTags = scriptTags.map((scriptTag, index) => {
    const regex = /<script\s*([^>]*?)(?:\/>|>([\s\S]*?)<\/script>)/;
    const [, scriptProps = '', scriptContent = ''] = scriptTag.match(regex) ?? [];

    const props = scriptProps
      .trim()
      .split(/\s+/)
      .reduce((props: Record<string, any>, prop) => {
        const [key, value] = prop.split('=');
        if (key) {
          props[key] = value ? value.replace(/(^"|"$)/g, '') : true;
        }
        return props;
      }, {});

    return (
      <Script
        id={`${dataContentIndex}-${index}`}
        key={`${dataContentIndex}-${index}`}
        dangerouslySetInnerHTML={{ __html: scriptContent }}
        {...props}
      />
    );
  });

  return transformedScriptTags;
};

type Script = string;
type Markup = string;

const extractScripts = (markup: string): [Markup, Script[]] => {
  const scriptRegex = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
  const scriptMatches = markup?.match(scriptRegex) || [];

  const markupWithoutScripts = markup?.replace(scriptRegex, '');

  return [markupWithoutScripts, scriptMatches];
};

const MarkupComponent: Component<MarkupProps> = ({ variant, markup, className, ...props }) => {
  //@ts-ignore
  const dataContentIndex = props['data-content-index'];
  const getPropStyles = getPropStylesFactory(c);

  const variantClassName = getPropStyles('variant', variant, 'default');
  const componentClassName = cn(variantClassName, className);

  if (!markup) {
    return null;
  }

  const [html, scripts] = extractScripts(markup);

  return (
    <>
      {html && <div dangerouslySetInnerHTML={{ __html: html }} className={componentClassName} {...props} />}
      {transformScriptTags(dataContentIndex, scripts)}
    </>
  );
};

export const Markup = MarkupComponent;
